import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { AspenOrdersService } from '../client'
import { createTaskSocket } from './useTaskSocket'

const GENERIC_ERROR = 'Failed to resend notification. Please try again.'

// Pull a human-readable message off an aspen API error (FastAPI puts a string
// in `body.detail` for our HTTPExceptions; slowapi 429s differ, so fall back).
const extractDetail = (err: unknown): string => {
    if (typeof err === 'object' && err !== null && 'body' in err) {
        const body = (err as { body?: unknown }).body
        if (typeof body === 'object' && body !== null && 'detail' in body) {
            const detail = (body as { detail?: unknown }).detail
            if (typeof detail === 'string' && detail.length > 0) {
                return detail
            }
        }
    }
    return GENERIC_ERROR
}

/**
 * Resend the Greenlight notification email for an order (APP-84/85).
 *
 * The POST dispatches a worker task and returns a task id; the actual result
 * arrives over the task WebSocket. The worker RETURNS `{ ok, error? }` instead
 * of raising on an origin failure, so the task `status` is `SUCCESS` even when
 * the send failed — success/failure is therefore read from `result.ok`, not
 * `status`. `loading` stays true from dispatch until the task settles, so the
 * confirmation modal can show progress and close on success.
 */
export const useResendGreenlightNotification = () => {
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const { enqueueSnackbar } = useSnackbar()

    const resendNotification = (orderId: string) => {
        setLoading(true)
        setError(null)
        AspenOrdersService.resendNotificationOrdersOrderIdResendNotificationPost({ orderId })
            .then((data) => {
                if (data && 'taskId' in data && data.taskId) {
                    setSocket(createTaskSocket(data.taskId))
                } else {
                    setLoading(false)
                }
            })
            .catch((err: unknown) => {
                const message = extractDetail(err)
                setError(message)
                enqueueSnackbar(message, { variant: 'error' })
                setLoading(false)
            })
    }

    useEffect(() => {
        if (!socket) return

        const finishWithError = (message: string) => {
            setError(message)
            enqueueSnackbar(message, { variant: 'error' })
            setLoading(false)
        }

        const handleMessage = (event: MessageEvent) => {
            let message: { status?: string; error?: string; result?: { ok?: boolean; error?: string } }
            try {
                message = JSON.parse(event.data)
            } catch {
                return
            }

            if (message.error) {
                finishWithError(message.error)
            } else if (message.status === 'SUCCESS') {
                if (message.result?.ok) {
                    enqueueSnackbar('Notification sent successfully to patient', { variant: 'success' })
                    setLoading(false)
                } else {
                    finishWithError(message.result?.error ?? GENERIC_ERROR)
                }
            } else if (message.status === 'FAILURE') {
                finishWithError(message.result?.error ?? GENERIC_ERROR)
            }
            // PENDING / STARTED / RETRY: task still running, keep loading.
        }

        const handleError = () => finishWithError(GENERIC_ERROR)

        socket.addEventListener('message', handleMessage)
        socket.addEventListener('error', handleError)

        return () => {
            socket.removeEventListener('message', handleMessage)
            socket.removeEventListener('error', handleError)
            socket.close()
        }
    }, [socket, enqueueSnackbar])

    return { resendNotification, loading, error }
}
